import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Tooltip as MuiTooltip,
  Typography
} from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { Add, AssignmentOutlined, BarChart as BarChartIcon, GetApp, HowToReg, InfoOutlined, PeopleOutline } from '@material-ui/icons'
import axios from 'axios'
import { useState } from 'react'
import { toast } from 'react-toastify'
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import CustomDatePicker from '../../../Applicants/Components/Filters/CustomDatePicker'

import { useRef } from 'react'
import { PersonAddOutlined, PersonOutline, PersonAddDisabledOutlined, EventAvailableOutlined, ExpandMore } from '@material-ui/icons'
import { LabelList } from 'recharts'

const DEFAULT_LOCATIONS = ['Prishtine HQ', 'Ferizaj', 'Vushtrri', 'Prizren', 'HO-Prishtine', 'HO-Ferizaj', 'HO-Vushtrri', 'HO-Prizren']
const DEFAULT_REQUEST_TYPES = ['Backfill', 'Ramp-Up']
const CHART_PALETTES = [
  ['#343434', '#165aff', '#7735e5', '#04a0be'],
  ['#f08323', '#008cbd', '#7735e5', '#15b58e'],
  ['#dd2518', '#0796b1', '#803cf0', '#ef9c18'],
  ['#008cbd', '#83b20a']
]
const HIRING_LANGUAGES = [
  { key: 'de', title: 'Pozitat në Gjermanisht', names: ['gjerman', 'german', 'deutsch'], tone: 'slate' },
  { key: 'fr', title: 'Pozitat në Frëngjisht', names: ['freng', 'french', 'franc'], tone: 'indigo' },
  { key: 'it', title: 'Pozitat në Italisht', names: ['ital'], tone: 'lime' }
]
let dashboardInstance = 0
const normalize = (value) => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
const formatNumber = (value) => value == null ? '—' : Number(value).toLocaleString('en-US', { maximumFractionDigits: 1 })
const shadeColor = (color) => '#' + color.slice(1).match(/../g).map((channel) => Math.round(parseInt(channel, 16) * 0.72).toString(16).padStart(2, '0')).join('')
const truncateLabel = (value) => String(value || '').length > 16 ? String(value).slice(0, 15) + '…' : value
const estimateChartLabelWidth = (value) => Array.from(String(value || '')).reduce((width, character) => {
  if ('MW@#%&'.includes(character)) return width + 10
  if ("ilI1.,' ".includes(character)) return width + 3.5
  return width + 6.5
}, 0)
const shouldUseChartInitials = (data, chartWidth) => {
  if (!chartWidth || !data.length) return false
  const plotWidth = Math.max(chartWidth - 50, 0)
  const categoryWidth = plotWidth / data.length
  return data.some(({ name }) => estimateChartLabelWidth(truncateLabel(name)) > categoryWidth - 6)
}
const chartLabelInitial = (value) => Array.from(String(value || '').trim())[0] || ''
const getRemainingDays = (value) => {
  if (!value) return '—'
  const deadline = new Date(value)
  if (Number.isNaN(deadline.getTime())) return '—'
  const today = new Date()
  return Math.round((Date.UTC(deadline.getFullYear(), deadline.getMonth(), deadline.getDate()) -
    Date.UTC(today.getFullYear(), today.getMonth(), today.getDate())) / 86400000)
}

const StaffingRequestStatusStats = ({
  availableProjects = [],
  availableDepartments = [],
  availableLanguages = [],
  availableLocations = DEFAULT_LOCATIONS,
  availableRequestTypes = DEFAULT_REQUEST_TYPES,
  dashboardStats = {},
  filterOptions = {},
  onFilterChange,
  onProjectChange,
  onAddNew,
  showAddButton = false,
  chartScope = 'total',
  onChartScopeChange,
  statsLoading = false
}) => {
  const [loading, setLoading] = useState(false)
  const [activeTooltip, setActiveTooltip] = useState(null)
  const [showAllDepartments, setShowAllDepartments] = useState(false)
  const [departmentModalOpen, setDepartmentModalOpen] = useState(false)
  const [chartWidths, setChartWidths] = useState({})
  const instance = useRef(null)
  if (instance.current === null) instance.current = ++dashboardInstance
  const kpis = dashboardStats.kpis || {}
  const hiring = dashboardStats.timeToHire || {}
  const departments = dashboardStats.topDepartments || []
  const departmentChartData = [...(dashboardStats.byDepartment || [])].sort((a, b) => b.total - a.total)
  const rows = showAllDepartments ? departments : departments.slice(0, 3)
  const hiringTooltip = 'Mesatarja e ditëve kalendarike nga krijimi i kërkesës deri te rekrutimi. Përfshin vetëm rekrutimet me histori të vlefshme.'

  const buildCsvParams = () => {
    const params = {}
    if (filterOptions.date_from) params.date_from = filterOptions.date_from
    if (filterOptions.date_to) params.date_to = filterOptions.date_to
    if (filterOptions.location?.length) params.location = filterOptions.location.join(',')
    if (filterOptions.department) params.department = String(filterOptions.department)
    if (filterOptions.project && !filterOptions.department) params.project = String(filterOptions.project)
    if (filterOptions.language) params.language = String(filterOptions.language)
    if (filterOptions.request_type) params.request_type = String(filterOptions.request_type)
    return params
  }

  const downloadCsv = async () => {
    setLoading(true)
    try {
      const response = await axios.get('/staffingRequest/download-csv', { params: buildCsvParams(), responseType: 'blob' })
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv;charset=utf-8;' }))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'staffing-request-stats-' + new Date().toISOString().slice(0, 10) + '.csv')
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Error downloading CSV:', err)
      toast.error('Nuk u mund te shkarkohet CSV')
    } finally {
      setLoading(false)
    }
  }

  const info = (title, text) => (
    <MuiTooltip
      title={text}
      arrow
      placement='top-start'
      open={activeTooltip === title}
      disableFocusListener
      disableHoverListener
      disableTouchListener
      componentsProps={{
        tooltip: {
          sx: {
            m: 0,
            p: '5px 10px',
            maxWidth: '280px',
            color: '#171717',
            background: '#fff',
            border: '1px solid #dfe3e8',
            borderRadius: '5px',
            boxShadow: '0 3px 10px rgba(15, 23, 42, 0.16)',
            fontFamily: "'source_sans_proregular', 'Source Sans Pro', sans-serif",
            fontSize: '12px',
            fontWeight: 400,
            lineHeight: '16px',
            letterSpacing: 0
          }
        },
        arrow: {
          sx: {
            color: '#fff',
            '&::before': {
              border: '1px solid #dfe3e8',
              boxSizing: 'border-box'
            }
          }
        }
      }}
    >
      <Box
        component='button'
        type='button'
        aria-label={title + ' — informacion'}
        onMouseEnter={() => setActiveTooltip(title)}
        onMouseLeave={() => setActiveTooltip(null)}
        onClick={() => setActiveTooltip((current) => current === title ? null : title)}
        sx={{
          border: 0,
          p: 0,
          ml: '3px',
          background: 'none',
          display: 'inline-flex',
          flexShrink: 0,
          alignItems: 'center',
          alignSelf: 'center',
          verticalAlign: 'middle',
          cursor: 'help',
          color: '#818b9a',
          '& .MuiSvgIcon-root': { width: '12px', height: '12px' },
          '&:focus-visible': { outline: '2px solid #147dff', outlineOffset: '3px', borderRadius: '3px' }
        }}
      >
        <InfoOutlined />
      </Box>
    </MuiTooltip>
  )

  const metric = ({ title, value, tone, Icon, iconSrc, tooltip, flag, description, days = false, primary = false, hiringCard = false }) => {
    const colors = tone === 'blue' ? ['#1681ff', 'rgba(15, 120, 255, 0.25)', '#D8E9FF', '#EAF5FF']
      : tone === 'purple' ? ['#883eff', 'rgba(125, 58, 237, 0.25)', '#E8DAFF', '#F6F0FF']
        : tone === 'green' ? ['#25cd96', 'rgba(22, 163, 74, 0.25)', '#C8FFDC', '#E8FFF1']
          : tone === 'red' ? ['#ff575f', 'rgba(255, 90, 80, 0.25)', '#FFD8D7', '#FFE6E6']
            : tone === 'cyan' ? ['#00a2c7', 'rgba(6, 146, 179, 0.25)', '#C7F4FF', '#EAFBFF']
              : tone === 'slate' ? ['#394353', 'rgba(55, 65, 81, 0.25)', '#DADFE8', '#EEF1F5']
                : tone === 'indigo' ? ['#2162ff', 'rgba(21, 93, 252, 0.25)', '#C4D6FF', '#E6EEFF']
                  : ['#26b631', 'rgba(36, 173, 40, 0.25)', '#D4FFD5', '#E8FFE9']
    const flagBackground = flag === 'de'
      ? 'linear-gradient(#161616 33.33%, #e5242a 33.33%, #e5242a 66.66%, #ffd633 66.66%)'
      : flag === 'fr'
        ? 'linear-gradient(90deg, #0057aa 33.33%, #fff 33.33%, #fff 66.66%, #e62332 66.66%)'
        : 'linear-gradient(90deg, #66ad4a 33.33%, #fff 33.33%, #fff 66.66%, #e32b39 66.66%)'

    return (
      <Card
        variant='outlined'
        sx={{
          height: hiringCard ? '120px' : '100%',
          borderRadius: '12px',
          borderColor: colors[1],
          background: 'linear-gradient(' + (primary || hiringCard ? 'to bottom' : '115deg') + ', ' + colors[2] + ', ' + colors[3] + ')',
          color: colors[0],
          boxShadow: 'none'
        }}
      >
        <CardContent
          sx={{
            display: 'flex',
            alignItems: primary || hiringCard ? 'center' : 'flex-start',
            justifyContent: 'space-between',
            height: '100%',
            boxSizing: 'border-box',
            p: { xs: '15px 16px', lg: '12px', xl: '15px 16px' },
            '&:last-child': { pb: { xs: '15px', lg: '12px', xl: '15px' } }
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              width: '100%',
              gap: { xs: '12px', lg: '6px', xl: '12px' }
            }}
          >
            <Box sx={{ minWidth: 0 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                color: '#171717',
                fontFamily: primary || hiringCard ? "'source_sans_prosemibold', 'Source Sans Pro', sans-serif" : 'inherit',
                fontSize: primary || hiringCard ? '15px' : { xs: '12px', lg: '10px', xl: '12px' },
                fontWeight: '700 !important',
                lineHeight: primary || hiringCard ? '16px' : 1.35
              }}
            >
              <Box component='span' sx={{ minWidth: 0 }}>{title}</Box>
              {info(title, tooltip)}
            </Box>
            <Box
              sx={{
                fontFamily: primary ? "'source_sans_prosemibold', 'Source Sans Pro', sans-serif" : 'inherit',
                fontSize: primary ? '32px' : { xs: '28px', lg: '25px', xl: '28px' },
                lineHeight: primary ? '32px' : 1.15,
                fontWeight: primary ? '700 !important' : 800,
                mt: '4px',
                letterSpacing: primary ? 0 : '-0.6px'
              }}
            >
              {formatNumber(value)}
              {days && <Box component='span' sx={{ fontSize: '12px', fontWeight: 700, letterSpacing: 0, ml: '6px' }}>Ditë</Box>}
            </Box>
            {description && (
              <Box
                sx={{
                  mt: '5px',
                  color: '#4b5563',
                  fontFamily: "'source_sans_proregular', 'Source Sans Pro', sans-serif",
                  fontSize: '13px',
                  fontWeight: 400,
                  lineHeight: '16.5px',
                  letterSpacing: 0
                }}
              >
                {description}
              </Box>
            )}
            </Box>
            {iconSrc ? (
              <Box
                component='img'
                src={`${process.env.PUBLIC_URL}/img/${iconSrc}`}
                alt=''
                aria-hidden='true'
                sx={{ flex: '0 0 28px', width: '28px', height: '28px' }}
              />
            ) : flag ? (
              <Box
                component='span'
                aria-hidden='true'
                sx={{
                  flex: { xs: '0 0 24px', lg: '0 0 20px', xl: '0 0 24px' },
                  width: { xs: '24px', lg: '20px', xl: '24px' },
                  height: { xs: '24px', lg: '20px', xl: '24px' },
                  borderRadius: '50%',
                  boxShadow: '0 0 0 1px #00000004',
                  background: flagBackground
                }}
              />
            ) : (
              <Icon style={{ flex: '0 0 auto', width: 24, height: 24, marginTop: 2 }} />
            )}
          </Box>
        </CardContent>
      </Card>
    )
  }

  const selectFilter = (label, key, options, onChange, disabled = false) => (
    <FormControl
      size='small'
      sx={{
        '& .MuiOutlinedInput-root': {
          height: '40px',
          fontFamily: "'source_sans_prosemibold', 'Source Sans Pro', sans-serif",
          fontSize: '14px',
          fontWeight: 600,
          lineHeight: '14px',
          letterSpacing: 0,
          color: '#4b5563',
          borderRadius: '6px',
          background: '#fff'
        },
        '& .MuiSelect-select': {
          display: 'flex',
          alignItems: 'center',
          height: '40px',
          boxSizing: 'border-box',
          p: '0 28px 0 10px !important',
          fontFamily: "'source_sans_prosemibold', 'Source Sans Pro', sans-serif",
          fontSize: '14px',
          fontWeight: 600,
          lineHeight: '14px',
          letterSpacing: 0
        },
        '& .MuiOutlinedInput-root > fieldset.MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root > fieldset.MuiOutlinedInput-notchedOutline:last-child': {
          m: 0,
          p: '0 8px',
          border: '1px solid #dfe3e8 !important'
        },
        '& .MuiOutlinedInput-root:hover > fieldset.MuiOutlinedInput-notchedOutline': { borderColor: '#c5cbd4 !important' },
        '& .MuiOutlinedInput-root.Mui-focused > fieldset.MuiOutlinedInput-notchedOutline': {
          borderColor: '#147dff !important',
          borderWidth: '1px !important'
        },
        '& .MuiSelect-icon': { color: '#8b95a6', fontSize: '17px', right: '8px' },
        '& .Mui-disabled .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e6ec !important' }
      }}
    >
      <Select
        displayEmpty
        value={filterOptions[key] || ''}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
        inputProps={{ 'aria-label': label }}
        IconComponent={ExpandMore}
        renderValue={(selected) => options.find((option) => option.value === selected)?.label || label}
      >
        <MenuItem value=''><em>Te gjitha</em></MenuItem>
        {options.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
      </Select>
    </FormControl>
  )

  const renderChart = (title, data, index, { action, chartKey = index, height = { xs: '220px', sm: '180px', xl: '218px' } } = {}) => {
    const chartData = data || []
    const palette = CHART_PALETTES[index]
    const prefix = 'recruitment-chart-' + instance.current + '-' + chartKey
    const useInitials = shouldUseChartInitials(chartData, chartWidths[chartKey])
    const formatChartLabel = (value) => useInitials ? chartLabelInitial(value) : truncateLabel(value)
    const handleChartResize = (width) => {
      const roundedWidth = Math.round(width)
      setChartWidths((current) => current[chartKey] === roundedWidth ? current : { ...current, [chartKey]: roundedWidth })
    }

    return (
      <Card variant='outlined' sx={{ borderColor: '#e1e5ec', borderRadius: '7px', boxShadow: '0 1px 1px #0f172a03' }}>
        <CardContent sx={{ p: { xs: '18px', lg: '14px', xl: '18px' }, '&:last-child': { pb: '12px' } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
            <Typography component='h3' sx={{ m: 0, fontFamily: "'source_sans_prosemibold', 'Source Sans Pro', sans-serif", fontSize: '16px', lineHeight: 1.3, fontWeight: 600 }}>
              {title}
            </Typography>
            {action}
          </Box>
          <Box sx={{ height, mt: '4px' }} aria-label={title} aria-busy={statsLoading}>
            {statsLoading ? (
              <Box role='status' sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#89919e', fontSize: '12px' }}>Loading…</Box>
            ) : chartData.length === 0 ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#89919e', fontSize: '12px' }}>Nuk ka të dhëna</Box>
            ) : (
              <ResponsiveContainer width='100%' height='100%' onResize={handleChartResize}>
                <BarChart data={chartData} margin={{ top: 18, right: 4, bottom: 0, left: -18 }} barCategoryGap='24%'>
                  <defs>
                    {palette.map((color, colorIndex) => (
                      <linearGradient key={color} id={prefix + '-' + colorIndex} x1='0' y1='0' x2='0' y2='1'>
                        <stop offset='0%' stopColor={color} />
                        <stop offset='100%' stopColor={shadeColor(color)} />
                      </linearGradient>
                    ))}
                  </defs>
                  <CartesianGrid stroke='#edf0f4' vertical={false} />
                  <XAxis dataKey='name' tickFormatter={formatChartLabel} interval={0} axisLine={false} tickLine={false} height={28} tick={{ fill: '#171717', fontFamily: 'source_sans_prosemibold, Source Sans Pro, sans-serif', fontSize: 12, fontWeight: 600 }} />
                  <YAxis allowDecimals={false} axisLine={false} tickLine={false} tick={{ fill: '#a4acb8', fontSize: 10 }} />
                  <Tooltip cursor={{ fill: '#f5f7fa' }} contentStyle={{ border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 12 }} />
                  <Bar dataKey='total' name='Pozita' maxBarSize={40} radius={[6, 6, 3, 3]}>
                    {chartData.map((entry, barIndex) => <Cell key={entry.name + '-' + barIndex} fill={'url(#' + prefix + '-' + (barIndex % palette.length) + ')'} />)}
                    <LabelList dataKey='total' position='top' style={{ fill: '#171717', fontFamily: 'source_sans_prosemibold, Source Sans Pro, sans-serif', fontSize: 12, fontWeight: 600 }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </Box>
        </CardContent>
      </Card>
    )
  }

  return (
    <Box
      component='section'
      aria-label='Recruitment overview'
      sx={{
        color: '#171717',
        background: '#f7f8fa',
        p: { xs: '16px', md: '20px' },
        m: { xs: '-15px -15px 24px', md: '-20px -20px 32px' },
        fontSize: '12px'
      }}
    >
      <Box
        component='header'
        sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: '12px', sm: '16px' },
          minHeight: { xs: 'auto', sm: '46px' },
          mt: { xs: '6px', md: '25px' },
          mb: '18px'
        }}
      >
        <Box>
          <Typography
            component='h1'
            sx={{
              m: 0,
              fontFamily: "'source_sans_prosemibold', 'Source Sans Pro', sans-serif",
              fontSize: '24px',
              lineHeight: '28px',
              fontWeight: 600
            }}
          >
            Recruitment Overview
          </Typography>
          <Typography
            component='p'
            sx={{
              m: 0,
              mt: '3px',
              fontFamily: "'source_sans_proregular', 'Source Sans Pro', sans-serif",
              fontSize: '14px',
              lineHeight: '100%',
              fontWeight: 400,
              color: '#89919e'
            }}
          >
            Track recruitment needs and workforce approval status.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
          {showAddButton && (
            <Button
              variant='contained'
              disableElevation
              startIcon={(
                <Box
                  component='img'
                  src={`${process.env.PUBLIC_URL}/img/plus.png`}
                  alt=''
                  aria-hidden='true'
                  sx={{ width: '16px', height: '16px', display: 'block' }}
                />
              )}
              onClick={onAddNew}
              sx={{
                height: '40px',
                minHeight: '40px',
                maxHeight: '40px',
                minWidth: 0,
                p: '8px 12px',
                gap: '8px',
                borderRadius: '6px',
                fontFamily: "'source_sans_prosemibold', 'Source Sans Pro', sans-serif",
                fontSize: '12px',
                fontWeight: 600,
                fontStyle: 'semi-bold',
                textTransform: 'none',
                boxShadow: 'none',
                background: '#147dff',
                '&:hover': { background: '#096ce9', boxShadow: 'none' },
                '& .MuiButton-startIcon': { ml: 0, mr: '6px' },
                '& .MuiButton-startIcon .MuiSvgIcon-root': { fontSize: '14px' }
              }}
            >
              Add New
            </Button>
          )}
          <Button
            variant='outlined'
            startIcon={(
              <Box
                component='img'
                src={`${process.env.PUBLIC_URL}/img/download.png`}
                alt=''
                aria-hidden='true'
                sx={{ width: '14px', height: '14px', display: 'block' }}
              />
            )}
            onClick={downloadCsv}
            disabled={loading}
            sx={{
              height: '40px',
              minHeight: '40px',
              maxHeight: '40px',
              minWidth: 0,
              p: '8px 12px',
              gap: '8px',
              borderRadius: '6px',
              fontFamily: "'source_sans_prosemibold', 'Source Sans Pro', sans-serif",
              fontSize: '12px',
              fontWeight: 600,
              fontStyle: 'semi-bold',
              textTransform: 'none',
              boxShadow: 'none',
              color: '#171717',
              background: '#fff',
              borderColor: '#e8ebf0',
              '&:hover': { background: '#f1f5f9', borderColor: '#cbd5e1' },
              '& .MuiButton-startIcon': { ml: 0, mr: '6px' },
              '& .MuiButton-startIcon .MuiSvgIcon-root': { fontSize: '14px' }
            }}
          >
            {loading ? 'Downloading…' : 'Download CSV'}
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(5, minmax(0, 1fr))' },
          gap: { xs: '8px', sm: '12px' },
          mb: '12px',
          '& > :last-child': { gridColumn: { xs: '1 / -1', md: 'auto' } }
        }}
      >
        {selectFilter('Project', 'project', availableProjects.map((item) => ({ value: item._id, label: item.name })), onProjectChange)}
        {selectFilter('Department', 'department', availableDepartments.map((item) => ({ value: item._id, label: item.name })), (value) => onFilterChange('department', value), !filterOptions.project)}
        <FormControl
          size='small'
          sx={{
            '& .MuiOutlinedInput-root': {
              height: '40px',
              fontFamily: "'source_sans_prosemibold', 'Source Sans Pro', sans-serif",
              fontSize: '14px',
              fontWeight: 600,
              lineHeight: '14px',
              letterSpacing: 0,
              color: '#4b5563',
              borderRadius: '6px',
              background: '#fff'
            },
            '& .MuiSelect-select': {
              display: 'flex',
              alignItems: 'center',
              height: '40px',
              boxSizing: 'border-box',
              p: '0 28px 0 10px !important',
              fontFamily: "'source_sans_prosemibold', 'Source Sans Pro', sans-serif",
              fontSize: '14px',
              fontWeight: 600,
              lineHeight: '14px',
              letterSpacing: 0
            },
            '& .MuiOutlinedInput-root > fieldset.MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root > fieldset.MuiOutlinedInput-notchedOutline:last-child': {
              m: 0,
              p: '0 8px',
              border: '1px solid #dfe3e8 !important'
            },
            '& .MuiOutlinedInput-root:hover > fieldset.MuiOutlinedInput-notchedOutline': { borderColor: '#c5cbd4 !important' },
            '& .MuiOutlinedInput-root.Mui-focused > fieldset.MuiOutlinedInput-notchedOutline': {
              borderColor: '#147dff !important',
              borderWidth: '1px !important'
            },
            '& .MuiSelect-icon': { color: '#8b95a6', fontSize: '17px', right: '8px' }
          }}
        >
          <Select
            multiple
            displayEmpty
            value={filterOptions.location || []}
            onChange={(event) => onFilterChange('location', event.target.value)}
            input={<OutlinedInput inputProps={{ 'aria-label': 'Location' }} />}
            IconComponent={ExpandMore}
            renderValue={(selected) => selected.length ? selected.join(', ') : 'Location'}
          >
            {availableLocations.map((location) => (
              <MenuItem key={location} value={location}>
                <Checkbox checked={(filterOptions.location || []).includes(location)} />
                <ListItemText primary={location} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {selectFilter('Language', 'language', availableLanguages.map((item) => ({ value: item._id, label: item.name })), (value) => onFilterChange('language', value))}
        {selectFilter('Request type', 'request_type', availableRequestTypes.map((value) => ({ value, label: value })), (value) => onFilterChange('request_type', value))}
      </Box>

      <Box sx={{ position: 'relative', zIndex: 0, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' }, gridAutoRows: '98px', gap: { xs: '12px', lg: '16px', xl: '20px' } }}>
        {metric({ title: 'Pozita të kërkuara', value: kpis.requestedPositions ?? 0, tone: 'blue', Icon: PeopleOutline, iconSrc: 'Users.png', primary: true, tooltip: 'Shuma e pozitave të kërkuara vetëm në kërkesat aktive (approved) dhe të skaduara (overdue). Kërkesat në pritje, të përfunduara dhe të refuzuara nuk përfshihen.' })}
        {metric({ title: 'Pozita të rekrutuara', value: kpis.recruitedPositions ?? 0, tone: 'purple', Icon: PersonAddOutlined, iconSrc: 'UserCog.png', primary: true, tooltip: 'Shuma e pozitave të rekrutuara vetëm në kërkesat aktive (approved) dhe të skaduara (overdue). Kërkesat në pritje, të përfunduara dhe të refuzuara nuk përfshihen.' })}
        {metric({ title: 'Pozita të hapura', value: kpis.openPositions ?? 0, tone: 'green', Icon: HowToReg, iconSrc: 'UserCheck.png', primary: true, tooltip: 'Shuma e diferencës mes pozitave të kërkuara dhe të rekrutuara, jo më pak se zero, vetëm për kërkesat aktive (approved) dhe të skaduara (overdue).' })}
        {metric({ title: 'Pozita të skaduara', value: kpis.overdueOpenPositions ?? 0, tone: 'red', Icon: PersonAddDisabledOutlined, iconSrc: 'UserPlus.png', primary: true, tooltip: 'Pozitat ende të hapura vetëm në kërkesat e skaduara (overdue). Ky numër është pjesë e “Pozita të hapura” dhe nuk duhet të mblidhet veçmas me të.' })}
      </Box>

      <Box component='section' aria-labelledby='recruitment-hiring-title' sx={{ mt: '22px' }}>
        <Typography component='h2' id='recruitment-hiring-title' sx={{ m: 0, mb: '12px', fontSize: '16px', fontWeight: 600 }}>Koha deri në punësim</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' }, gap: { xs: '12px', lg: '16px', xl: '20px' } }}>
          {metric({
            title: 'Mesatarja e përgjithshme', value: hiring.overall?.averageDays, tone: 'cyan', Icon: EventAvailableOutlined, iconSrc: 'CalendarClock.png', hiringCard: true,
            tooltip: hiringTooltip, days: true,
            description: hiring.overall ? formatNumber(hiring.overall.recruited) + ' persona të rekrutuar' : 'Nuk ka të dhëna rekrutimi'
          })}
          {HIRING_LANGUAGES.map((language) => {
            const matchingRows = (hiring.byLanguage || []).filter((item) => normalize(item.code).split('-')[0] === language.key || language.names.some((name) => normalize(item.name).includes(name)))
            const count = matchingRows.reduce((sum, item) => sum + item.recruited, 0)
            const average = count ? matchingRows.reduce((sum, item) => sum + item.averageDays * item.recruited, 0) / count : null
            return (
              <Box key={language.key}>
                {metric({
                  title: language.title, value: average, tone: language.tone, flag: language.key, hiringCard: true,
                  tooltip: hiringTooltip, days: true,
                  description: count ? formatNumber(count) + ' persona të rekrutuar' : 'Nuk ka të dhëna rekrutimi'
                })}
              </Box>
            )
          })}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: '6px', width: { xs: '100%', sm: 'fit-content' }, maxWidth: '100%', p: '4px', m: '34px 0 24px', borderRadius: '8px', background: '#fff' }} role='group' aria-label='Chart status'>
        {[['total', 'Total', kpis.requestedPositions], ['active', 'Active', kpis.activeRequested], ['overdue', 'Overdue', kpis.overduePositions]].map(([key, label, count]) => (
          <Box
            key={key}
            component='button'
            type='button'
            aria-pressed={chartScope === key}
            onClick={() => onChartScopeChange?.(key)}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: { xs: '8px', sm: '28px' },
              minWidth: { xs: 0, sm: '124px' },
              flex: { xs: 1, sm: 'initial' },
              p: '5px 10px',
              border: '1px solid',
              borderColor: chartScope === key ? '#1482ff' : '#eff0f4',
              borderRadius: '5px',
              background: chartScope === key ? '#eaf3ff' : '#f7f8fa',
              color: chartScope === key ? '#171717' : '#89919e',
              fontFamily: "'source_sans_prosemibold', 'Source Sans Pro', sans-serif",
              fontSize: '13px',
              lineHeight: '15px',
              fontWeight: 700,
              cursor: 'pointer',
              '&:focus-visible': { outline: '2px solid #147dff', outlineOffset: '2px' }
            }}
          >
            {label}<Box component='span' sx={{ color: chartScope === key ? '#147dff' : 'inherit' }}>{formatNumber(count)}</Box>
          </Box>
        ))}
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' }, gap: { xs: '12px', lg: '16px', xl: '20px' } }}>
        {renderChart('Kërkesat sipas gjuhës', dashboardStats.byLanguage, 0)}
        {renderChart('Kërkesat sipas lokacionit', dashboardStats.byLocation, 1)}
        {renderChart('Departamentet', departmentChartData.slice(0, 4), 2, {
          action: <Button size='small' onClick={() => setDepartmentModalOpen(true)} sx={{ flexShrink: 0, fontSize: '11px', textTransform: 'none' }}>View All</Button>
        })}
        {renderChart('Lloji i kërkesës', dashboardStats.byRequestType, 3)}
      </Box>

      <Dialog open={departmentModalOpen} onClose={() => setDepartmentModalOpen(false)} fullWidth maxWidth={false} PaperProps={{ sx: { width: 'calc(100% - 32px)', maxWidth: '1400px', m: '16px' } }} aria-labelledby='all-departments-title'>
        <DialogTitle id='all-departments-title'>All departments</DialogTitle>
        <DialogContent>
          {renderChart('Departamentet', departmentChartData, 2, { chartKey: 'all-departments', height: 'min(55vh, 560px)' })}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDepartmentModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Box component='section' aria-labelledby='recruitment-departments-title' sx={{ mt: { xs: '30px', sm: '50px' } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', mb: '8px' }}>
          <Box>
            <Typography component='h2' id='recruitment-departments-title' sx={{ m: 0, fontSize: '18px', fontWeight: 600 }}>Highest remaining need</Typography>
            <Typography component='p' sx={{ m: 0, mt: '3px', fontSize: '11px', color: '#89919e' }}>Top departments by open positions</Typography>
          </Box>
          {departments.length > 3 && (
            <Button size='small' onClick={() => setShowAllDepartments((current) => !current)} sx={{ fontSize: '11px', textTransform: 'none' }}>
              {showAllDepartments ? 'Show top 3' : 'View all'}
            </Button>
          )}
        </Box>
        <Box sx={{ border: '1px solid #e4e8ef', borderRadius: '10px', overflowX: 'auto', background: '#fff' }}>
          <Box
            component='table'
            sx={{
              width: '100%',
              minWidth: '560px',
              tableLayout: 'fixed',
              borderCollapse: 'collapse',
              fontSize: '11px',
              '& th, & td': { p: '10px 12px', borderRight: '1px solid #e8ecf2', borderBottom: '1px solid #e8ecf2', textAlign: 'left', lineHeight: 1.35 },
              '& thead th': {
                color: '#7c8696',
                fontFamily: "'source_sans_prosemibold', 'Source Sans Pro', sans-serif",
                fontSize: '14px',
                fontWeight: 600,
                background: '#f8f9fb',
                pt: '13px',
                pb: '13px'
              },
              '& tbody th': {
                fontFamily: "'source_sans_prosemibold', 'Source Sans Pro', sans-serif",
                fontSize: '14px',
                fontWeight: 600
              },
              '& tbody td': {
                fontFamily: "'source_sans_prosemibold', 'Source Sans Pro', sans-serif",
                fontSize: '12px',
                fontWeight: 500
              },
              '& th:last-child, & td:last-child': { borderRight: 0 },
              '& tr:last-child > *': { borderBottom: 0 },
              '& tbody tr:first-of-type': { background: '#fff8f0' }
            }}
          >
            <thead><tr>{['Department', 'Requested', 'Recruited', 'Open', 'Remaining days'].map((label) => <th key={label} scope='col'>{label}</th>)}</tr></thead>
            <tbody>
              {rows.map((department, index) => (
                <tr key={department.name + '-' + index}>
                  <th scope='row'>
                    <Box
                      component='span'
                      sx={{
                        display: 'block',
                        color: '#89919e',
                        fontFamily: "'source_sans_prosemibold', 'Source Sans Pro', sans-serif",
                        fontSize: '12px',
                        fontWeight: 500
                      }}
                    >
                      # {index + 1}
                    </Box>
                    {department.name}
                  </th>
                  <td>{formatNumber(department.requested)}</td>
                  <td>{formatNumber(department.recruited)}</td>
                  <Box component='td' sx={{ color: '#ff524e', fontWeight: 500 }}>{formatNumber(department.remaining)}</Box>
                  <td>{getRemainingDays(department.nearestDeadline)}</td>
                </tr>
              ))}
              {!rows.length && (
                <tr>
                  <Box component='td' colSpan={5} sx={{ p: '28px !important', background: '#fff', textAlign: 'center !important', color: '#89919e' }}>
                    No active department demand
                  </Box>
                </tr>
              )}
            </tbody>
          </Box>
        </Box>
      </Box>

      <Box component='details' sx={{ mt: '18px', fontSize: '11px', color: '#89919e' }}>
        <Box component='summary' sx={{ width: 'fit-content', cursor: 'pointer' }}>Date range</Box>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box
            sx={{
              position: 'relative',
              zIndex: 1,
              display: 'grid',
              gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 220px))' },
              alignItems: 'start',
              gap: '16px',
              mt: '14px',
              pb: '2px',
              '& > *': { minWidth: 0 },
              '& .MuiFormControl-root': { width: '100%', overflow: 'visible' },
              '& .MuiOutlinedInput-root': {
                height: '40px',
                overflow: 'visible',
                borderRadius: '6px',
                background: '#fff'
              },
              '& .MuiOutlinedInput-root > fieldset.MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root > fieldset.MuiOutlinedInput-notchedOutline:last-child': {
                m: 0,
                p: '0 8px',
                border: '1px solid #dfe3e8 !important'
              },
              '& .MuiOutlinedInput-root:hover > fieldset.MuiOutlinedInput-notchedOutline': {
                borderColor: '#c5cbd4 !important'
              },
              '& .MuiOutlinedInput-root.Mui-focused > fieldset.MuiOutlinedInput-notchedOutline': {
                borderColor: '#147dff !important',
                borderWidth: '1px !important'
              }
            }}
          >
            <CustomDatePicker label='From Date' value={filterOptions.date_from} onChange={(value) => onFilterChange('date_from', value)} />
            <CustomDatePicker label='To Date' value={filterOptions.date_to} onChange={(value) => onFilterChange('date_to', value)} minDate={filterOptions.date_from} maxDate={Date.now()} />
          </Box>
        </LocalizationProvider>
      </Box>
    </Box>
  )
}

export default StaffingRequestStatusStats
